import config from "../config";
import BearerApi from "./bearer";
import _ from "lodash";
import Promise from "bluebird";
import dataURItoBlob from "./dataUriToBlob";
import { flatten } from "flat";

export class PrescriptionAlreadyIntegratedError extends Error {
  constructor(message = "prescription already integrated with this app") {
    super(message);
    this.name = "PrescriptionAlreadyIntegratedError";
  }
}

const baseUrl = config.apiUrl;
class Api extends BearerApi {
  constructor(token) {
    super(baseUrl, token);
  }

  me() {
    return this.get("/users/me");
  }

  createUser(userToCreate) {
    const body = new FormData();
    _(_.omit(userToCreate, "signature")).each((value, key) =>
      body.append(key, value)
    );
    const signatureFile = this._toSignatureFile(userToCreate.signature);
    body.append("signature", signatureFile);
    return this.post("/users", body);
  }

  createPrescription(body) {
    return this.post("/prescriptions", body);
  }

  createOrder(body) {
    return this.post("/orders", body);
  }

  medicalDocument(id) {
    return this.medicalDocuments({ filters: { id } })
      .then(({ data }) => _.first(data))
      .tap((medicalDocument) => {
        if (!medicalDocument) throw new Error("not found");
      });
  }

  medicalDocuments({ filters, page = 0, ...query }) {
    const filter = _(filters)
      .pickBy()
      .thru((filter) => flatten({ filter }))
      .value();
    return this.get("/medical-documents", {
      ...filter,
      ...query,
      page: page + 1,
    });
  }

  patientsDetails(id) {
    return this.get(`/patients/${id}`);
  }

  patients(query) {
    const isPaginated = _.has(query, "page") || _.has(query, "limit");
    return this.get(isPaginated ? "/patients/search" : "/patients", query);
  }

  share(ids, body) {
    return this.post(`/medical-documents/share`, _.assign({}, body, { ids }));
  }

  templates(type) {
    if (Array.isArray(type)) {
      return Promise.all(type.map((t) => this.get("/templates", { type: t }))).then(
        (results) => results.flat()
      );
    }
    return this.get("/templates", { type });
  }

  createTemplate(type, templateDto) {
    const body = _.assign({}, templateDto, { type });
    return this.post("/templates", body);
  }

  updateTemplate(id, body) {
    return this.put(`/templates/${id}`, body);
  }

  deleteTemplate(id) {
    return this.delete(`/templates/${id}`);
  }

  repeat(basePath, id, body) {
    return this.post(`/${basePath}/${id}/repeat`, body);
  }

  createPatient(patientDto) {
    const body = _.omit(patientDto, "id");
    return this.post("/patients", body);
  }

  updatePatient(id, patientDto) {
    const body = _.omit(patientDto, "id");
    return this.put(`/patients/${id}`, body);
  }

  updateUser(body) {
    const { signature, ...userInfo } = body;
    return Promise.all([
      this.updateUserInfo(userInfo),
      this.updateUserSignature(signature),
    ]);
  }

  updateUserInfo(body) {
    if (_.isEmpty(body)) return Promise.resolve();
    return this.put("/users/me", body);
  }

  updateUserSignature(signature) {
    if (!signature) return Promise.resolve();
    const signatureFile = this._toSignatureFile(signature);
    const formData = new FormData();
    formData.append("signature", signatureFile);
    return this.put("/users/me/signature", formData);
  }

  signature() {
    return this.get("/users/me/signature");
  }

  patientRegistrationToken() {
    return this.get("/users/me/patientsToken");
  }

  userByToken() {
    return this.get("/users/byPatientsToken");
  }

  cancelPrescription(id) {
    return this.post(`/prescriptions/${id}/cancel`);
  }

  healthInsurances() {
    return this.get("/patients/health-insurances");
  }

  medicationsFile() {
    return this.get("/medications/file");
  }

  createIntegration(id, body) {
    return this.post(`/prescriptions/${id}/integrations`, body)
      .catch((error) => {
        if (error?.response?.status === 409 && 
            _.includes(error?.response?.data?.message, "already integrated")) {
          throw new PrescriptionAlreadyIntegratedError();
        }
        throw error;
      });
  }

  _toSignatureFile(signatureDataUri) {
    const blob = dataURItoBlob(signatureDataUri);
    const signatureFile = new File([blob], "signature.jpg", {
      type: "image/jpeg",
    });
    return signatureFile;
  }
}

export default Api;
